import React, { useState, useEffect, useMemo } from 'react';
import { 
  useStripe, 
  useElements, 
  PaymentElement,
  Elements 
} from '@stripe/react-stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import { useStripeContext } from '../../../contexts/StripeContext';
import { PaymentService } from '../../../services/paymentService';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

import Icon from '../../../components/AppIcon';

// Inner form component that uses Stripe hooks
const PaymentFormInner = ({ 
  onNext, 
  onBack, 
  formData, 
  setFormData, 
  discountCode, 
  setDiscountCode, 
  discountApplied, 
  setDiscountApplied,
  eventData,
  selectedTier,
  quantity,
  total
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [showDemoNotice, setShowDemoNotice] = useState(true);

  // Auto-apply demo discount code on mount
  useEffect(() => {
    if (discountCode && discountCode === 'EARLY20' && !discountApplied) {
      setTimeout(() => {
        const validCodes = {
          'EARLY20': { discount: 20, type: 'percentage', description: 'Early Bird 20% Off' },
          'SAVE50': { discount: 50, type: 'fixed', description: '$50 Off' },
          'STUDENT15': { discount: 15, type: 'percentage', description: 'Student Discount 15% Off' }
        };
        
        if (validCodes?.[discountCode?.toUpperCase()]) {
          setDiscountApplied(validCodes?.[discountCode?.toUpperCase()]);
        }
      }, 1500); // Apply after 1.5 seconds for demo effect
    }
  }, [discountCode, discountApplied, setDiscountApplied]);

  // Initialize payment intent when component mounts and user data is available
  useEffect(() => {
    const initializePayment = async () => {
      if (!eventData || !selectedTier || !user || !formData?.firstName || !formData?.email) {
        return;
      }

      try {
        setErrors(prev => ({ ...prev, payment: '' }));
        
        const result = await PaymentService?.processBookingPayment(
          formData, 
          eventData, 
          selectedTier, 
          quantity, 
          user
        );

        if (result?.success) {
          setClientSecret(result?.data?.clientSecret);
          setPaymentIntentId(result?.data?.paymentIntentId);
          setPaymentReady(true);
        } else {
          setErrors(prev => ({
            ...prev,
            payment: result?.error || 'Failed to initialize payment system'
          }));
        }
      } catch (error) {
        console.error('Payment initialization failed:', error);
        setErrors(prev => ({
          ...prev,
          payment: 'Failed to initialize payment. Please try again.'
        }));
      }
    };

    initializePayment();
  }, [eventData, selectedTier, user, formData?.firstName, formData?.lastName, formData?.email, quantity]);

  // Check if payment element is ready
  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const paymentMethods = [
    { value: 'card', label: 'Credit/Debit Card', icon: 'CreditCard' },
    { value: 'paypal', label: 'PayPal', icon: 'Wallet' },
    { value: 'apple', label: 'Apple Pay', icon: 'Smartphone' },
    { value: 'google', label: 'Google Pay', icon: 'Smartphone' }
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1)?.padStart(2, '0'),
    label: String(i + 1)?.padStart(2, '0')
  }));

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date()?.getFullYear() + i;
    return { value: String(year), label: String(year) };
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev?.payment,
        [field]: value
      }
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDiscountApply = async () => {
    if (!discountCode?.trim()) return;
    
    setDiscountLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const validCodes = {
        'EARLY20': { discount: 20, type: 'percentage', description: 'Early Bird 20% Off' },
        'SAVE50': { discount: 50, type: 'fixed', description: '$50 Off' },
        'STUDENT15': { discount: 15, type: 'percentage', description: 'Student Discount 15% Off' }
      };
      
      if (validCodes?.[discountCode?.toUpperCase()]) {
        setDiscountApplied(validCodes?.[discountCode?.toUpperCase()]);
      } else {
        setErrors(prev => ({
          ...prev,
          discountCode: 'Invalid discount code'
        }));
      }
      setDiscountLoading(false);
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      setErrors(prev => ({
        ...prev,
        payment: 'Payment system not ready. Please wait and try again.'
      }));
      return;
    }

    if (!user) {
      setErrors(prev => ({
        ...prev,
        payment: 'Please sign in to complete your booking.'
      }));
      return;
    }

    if (!clientSecret) {
      setErrors(prev => ({
        ...prev,
        payment: 'Payment not initialized. Please refresh the page and try again.'
      }));
      return;
    }

    setIsProcessing(true);
    setErrors({});

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/booking-confirmation`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        console.error('Stripe payment error:', stripeError);
        setErrors(prev => ({
          ...prev,
          payment: stripeError?.message || 'Payment failed. Please try again.'
        }));
        return;
      }

      if (paymentIntent && paymentIntent?.status === 'succeeded') {
        // Complete booking after successful payment
        const completionResult = await PaymentService?.completeBooking(paymentIntentId, {
          eventId: eventData?.id,
          ticketId: selectedTier?.id,
          attendeeId: user?.id,
          quantity: quantity,
          totalAmount: total,
          attendeeInfo: {
            firstName: formData?.firstName,
            lastName: formData?.lastName,
            email: formData?.email,
            phone: formData?.phone,
            company: formData?.company || '',
            specialRequirements: formData?.specialRequirements || '',
            address: formData?.address || '',
            city: formData?.city || '',
            state: formData?.state || '',
            zipCode: formData?.zipCode || '',
            country: formData?.country || 'US'
          }
        });

        if (completionResult?.success) {
          // Store booking data for confirmation page
          setFormData(prev => ({
            ...prev,
            bookingResult: {
              booking: completionResult?.data?.booking,
              paymentDetails: completionResult?.data?.paymentDetails
            }
          }));
          
          onNext();
        } else {
          setErrors(prev => ({
            ...prev,
            payment: completionResult?.error || 'Booking creation failed. Please contact support.'
          }));
        }
      } else {
        setErrors(prev => ({
          ...prev,
          payment: 'Payment processing incomplete. Please try again.'
        }));
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        setErrors(prev => ({
          ...prev,
          payment: 'Cannot connect to payment service. Please check your internet connection and try again.'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          payment: 'An unexpected error occurred. Please try again.'
        }));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  // Configure PaymentElement with prefilled billing details from user data
  const paymentElementOptions = useMemo(() => ({
    fields: {
      billingDetails: {
        name: 'auto',
        email: 'auto',
        phone: 'auto',
        address: {
          line1: 'auto',
          line2: 'auto',
          city: 'auto',
          state: 'auto',
          postalCode: 'auto',
          country: 'auto'
        }
      }
    },
    defaultValues: {
      billingDetails: {
        name: formData?.firstName && formData?.lastName 
          ? `${formData?.firstName} ${formData?.lastName}` 
          : user?.full_name || '',
        email: formData?.email || user?.email || '',
        phone: formData?.phone || user?.phone || '',
        address: {
          line1: formData?.address || '',
          city: formData?.city || '',
          state: formData?.state || '',
          postal_code: formData?.zipCode || '',
          country: formData?.country || 'US'
        }
      }
    },
    layout: 'tabs'
  }), [formData, user]);

  // Show loading state while payment system initializes
  if (!stripe || !elements) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Initializing secure payment system...
        </p>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="AlertTriangle" size={24} className="text-destructive" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Payment Error</h2>
            <p className="text-sm text-muted-foreground">Unable to initialize payment system</p>
          </div>
        </div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <p className="text-destructive text-sm">Payment initialization failed. Please refresh the page and try again.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="sm:w-auto"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Info
          </Button>
          
          <Button
            type="button"
            variant="default"
            onClick={() => window.location?.reload()}
            className="sm:ml-auto sm:w-auto"
          >
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Retry Payment Setup
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background border border-border rounded-lg p-6">
      {/* Demo Notice Banner */}
      {showDemoNotice && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <Icon name="CreditCard" size={20} className="text-green-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-1">Demo Payment Form</h3>
                <p className="text-sm text-green-700">
                  Payment form is prefilled with billing details from your profile. 
                  A demo discount code has been applied automatically.
                  <br />
                  <strong>For testing:</strong> Use Stripe test card 4242 4242 4242 4242
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDemoNotice(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center space-x-3 mb-6">
        <Icon name="CreditCard" size={24} className="text-primary" />
        <div>
          <h2 className="text-xl font-semibold text-foreground">Payment Information</h2>
          <p className="text-sm text-muted-foreground">Secure payment processing with SSL encryption</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Discount Code Section */}
        <div className="p-4 bg-muted rounded-lg">
          <h3 className="text-sm font-medium text-foreground mb-3">Discount Code</h3>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Enter discount code"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e?.target?.value?.toUpperCase());
                if (errors?.discountCode) {
                  setErrors(prev => ({ ...prev, discountCode: '' }));
                }
              }}
              error={errors?.discountCode}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscountApply}
              loading={discountLoading}
              disabled={!discountCode?.trim() || discountApplied}
            >
              Apply
            </Button>
          </div>
          
          {discountApplied && (
            <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success">{discountApplied?.description}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDiscountApplied(null);
                    setDiscountCode('');
                  }}
                >
                  <Icon name="X" size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Stripe Payment Element */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Payment Details</h3>
          <div className="p-4 border border-border rounded-lg">
            <PaymentElement 
              options={paymentElementOptions}
            />
          </div>
        </div>

        {/* Error Message */}
        {errors?.payment && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive text-sm">{errors?.payment}</p>
          </div>
        )}

        {/* Security Badges */}
        <div className="flex items-center justify-center space-x-6 py-4 border-t border-border">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span className="text-sm">SSL Secured</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Lock" size={16} />
            <span className="text-sm">256-bit Encryption</span>
          </div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="CheckCircle" size={16} />
            <span className="text-sm">PCI Compliant</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isProcessing}
            className="sm:w-auto"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Info
          </Button>
          
          <Button
            type="submit"
            variant="default"
            loading={isProcessing}
            disabled={!stripe || !elements || !clientSecret || isProcessing}
            className="sm:ml-auto sm:w-auto"
          >
            {isProcessing ? 'Processing Payment...' : `Pay $${total?.toFixed(2)}`}
            {!isProcessing && <Icon name="ArrowRight" size={16} className="ml-2" />}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Wrapper component that provides Elements context
const PaymentForm = (props) => {
  const { stripePromise } = useStripeContext();
  const { total, formData, eventData, selectedTier, quantity } = props;
  const { user } = useAuth(); // Add user from AuthContext

  // State for Elements options including clientSecret
  const [elementsOptions, setElementsOptions] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [initializationError, setInitializationError] = useState(null);

  // Initialize payment intent and set up Elements options
  useEffect(() => {
    const initializePaymentElements = async () => {
      // Add user validation first
      if (!user) {
        setInitializationError('Please sign in to proceed with payment');
        setPaymentReady(false);
        return;
      }

      if (!eventData || !selectedTier || !formData?.firstName || !formData?.email || !total || total <= 0) {
        console.log('⏳ [INFO] Waiting for required data to initialize payment:', {
          hasEventData: !!eventData,
          hasSelectedTier: !!selectedTier,
          hasFormData: !!(formData?.firstName && formData?.email),
          hasValidTotal: total > 0,
          user: user ? 'Present' : 'Missing'
        });
        return;
      }

      try {
        console.log('🔧 [DEBUG] Initializing payment elements with data:', {
          eventId: eventData?.id,
          ticketId: selectedTier?.id,
          userId: user?.id,
          total: total
        });

        // Create payment intent to get clientSecret
        const result = await PaymentService?.processBookingPayment(
          formData, 
          eventData, 
          selectedTier, 
          quantity, 
          user
        );

        if (result?.success && result?.data?.clientSecret) {
          // Set Elements options with clientSecret
          const options = {
            clientSecret: result?.data?.clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0570de',
                colorBackground: '#ffffff',
                colorText: '#30313d',
                colorDanger: '#df1b41',
                fontFamily: 'Inter, system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '8px',
              }
            }
          };
          
          setElementsOptions(options);
          setPaymentReady(true);
          setInitializationError(null);
          
          console.log('✅ [SUCCESS] Stripe Elements initialized successfully');
          
          // Pass the clientSecret and paymentIntentId to the inner form
          props?.setFormData(prev => ({
            ...prev,
            paymentData: {
              clientSecret: result?.data?.clientSecret,
              paymentIntentId: result?.data?.paymentIntentId
            }
          }));
        } else {
          console.error('❌ [ERROR] Failed to initialize payment:', result?.error);
          setInitializationError(result?.error || 'Failed to initialize payment system');
          setPaymentReady(false);
        }
      } catch (error) {
        console.error('❌ [ERROR] Error initializing payment elements:', error);
        setInitializationError('Unable to connect to payment service. Please try again.');
        setPaymentReady(false);
      }
    };

    initializePaymentElements();
  }, [eventData?.id, selectedTier?.id, user?.id, formData?.firstName, formData?.email, total, quantity]);

  // Show error state if user not authenticated
  if (!user) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="UserX" size={24} className="text-destructive" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Authentication Required</h2>
            <p className="text-sm text-muted-foreground">Please sign in to proceed with payment</p>
          </div>
        </div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <p className="text-destructive text-sm">You must be signed in to complete your booking. Please return to the previous step or refresh the page.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={props?.onBack}
            className="sm:w-auto"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Info
          </Button>
          
          <Button
            type="button"
            variant="default"
            onClick={() => window.location.href = '/authentication'}
            className="sm:ml-auto sm:w-auto"
          >
            <Icon name="LogIn" size={16} className="mr-2" />
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Show initialization error state
  if (initializationError) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="AlertTriangle" size={24} className="text-destructive" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Payment Initialization Error</h2>
            <p className="text-sm text-muted-foreground">Unable to set up payment system</p>
          </div>
        </div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <p className="text-destructive text-sm">{initializationError}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={props?.onBack}
            className="sm:w-auto"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Info
          </Button>
          
          <Button
            type="button"
            variant="default"
            onClick={() => window.location?.reload()}
            className="sm:ml-auto sm:w-auto"
          >
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Retry Payment Setup
          </Button>
        </div>
      </div>
    );
  }

  // Show loading while initializing
  if (!elementsOptions || !paymentReady) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          {total > 0 ? `Preparing secure payment for $${total?.toFixed(2)}...` : 'Calculating total amount...'}
        </p>
      </div>
    );
  }

  return (
    <Elements 
      stripe={stripePromise} 
      options={elementsOptions}
    >
      <PaymentFormInner {...props} />
    </Elements>
  );
};

export default PaymentForm;